import { apiClient } from './client';

export interface CreateLeadDto {
  bride_name: string;
  groom_name: string;
  event_date?: string; // ISO date string - optional for backward compatibility
  plan_type: 'weekday' | 'weekend'; // Required - user selection
  guest_count: number;
  contact_email: string;
  country_code: string; // Required - country calling code (e.g., +57)
  contact_phone: string; // Phone number without country code
  referral_sources?: string[]; // Optional - marketing tracking
  privacy_policy_version?: string; // Public form only - records consent to the shown policy
  flow_token?: string; // Public form only - the arm decided on page visit (see quoteFlowAssignment)
}

export interface Lead {
  _id: string;
  bride_name: string;
  groom_name: string;
  event_date?: string; // Optional - for backward compatibility
  plan_type: 'weekday' | 'weekend'; // Required
  guest_count: number;
  over_capacity?: boolean; // more guests than the quote covers: no quote was sent
  contact_email: string;
  country_code?: string; // Optional - for backward compatibility with legacy data
  contact_phone: string;
  magic_token: string;
  token_expires_at: string;
  converted: boolean; // has an account and event (instant leads: from the reveal)
  converted_at?: string;
  // Converted under the shared definition: instant leads only once they
  // confirm their email. Sent by the admin leads list.
  conversion_confirmed?: boolean;
  template_used?: string;
  quote_pdf_url?: string;
  referral_sources?: string[]; // Optional - marketing tracking
  source?: 'website' | 'admin';
  privacy_consent_at?: string; // ISO date - public form consent (absent on admin/older leads)
  privacy_policy_version?: string;
  // Quote email delivery tracking (absent on older leads)
  quote_email_status?: 'sent' | 'delivered' | 'soft_bounced' | 'bounced' | 'spam' | 'unsubscribed';
  quote_email_status_at?: string; // ISO date
  quote_email_status_reason?: string; // Brevo bounce/block reason
  // Instant quote A/B (absent on older leads)
  quote_flow?: 'instant' | 'email';
  quote_flow_reason?: string;
  quote_info?: {
    quote_status?: 'preliminary' | 'saved';
    quote_saved_at?: string;
    email_verified_at?: string;
  };
  // Portal access control (per-lead)
  portal_access_enabled?: boolean;
  portal_access_enabled_at?: string;
  portal_access_enabled_by?: string;
  created_at: string;
  updated_at: string;
  // Legacy timestamp fields (for backward compatibility with old documents)
  createdAt?: string;
  updatedAt?: string;
  // Associated event status (only present if converted, used for field restrictions)
  associated_event?: {
    status: 'draft' | 'contract' | 'confirmed' | 'postponed' | 'completed' | 'cancelled';
    prices_frozen: boolean;
    confirmed_at?: string;
  } | null;
}

/** Sender and subject of the quote email, as the inbox shows them */
export interface QuoteEmailEnvelope {
  senderName: string;
  senderAddress: string;
  subject: string;
}

/** What the public confirmation screen may still do (enforced server-side) */
export interface QuoteConfirmationStatus {
  resendsLeft: number;
  emailChangesLeft: number;
  /** Seconds until the quote can be resent (0 = now) */
  cooldownSeconds: number;
}

/** What the server tells a returning visitor about the quote they submitted earlier */
export type ReturnStatus =
  | { converted: true }
  | {
      converted: false;
      firstName: string;
      /** Local part cut to two characters, full domain: `ma•••@gmail.com` */
      maskedEmail: string;
      submittedAt?: string;
      quoteEmailStatus?: string;
      portalAvailable: boolean;
    };

export interface LeadsListResponse {
  data: Lead[];
  total: number;
  page: number;
  limit: number;
  pagination?: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
  stats?: {
    pending: number;
    converted: number;
    globalTotal: number;
  };
}

export const leadsApi = {
  /** Public: decides this visitor's arm (instant | email) and signs it for the form submit */
  quoteFlow: (force?: 'instant' | 'email') =>
    apiClient.get<{ flow: 'instant' | 'email'; token: string; expiresAt: number; split?: number }>('/leads/quote-flow', {
      params: force && { force },
    }),
  create: (data: CreateLeadDto) =>
    apiClient.post<{
      success: boolean;
      data: QuoteConfirmationStatus & {
        id: string;
        /** Unlocks updateReferralSources for this lead for an hour */
        referral_token: string;
        /** Credential for resendQuote/changeContactEmail; absent if it could not be issued */
        confirmationToken?: string;
        /** Read-only credential for returnStatus(), kept on the device for ~30 days */
        returnToken?: string;
        quoteEmail?: QuoteEmailEnvelope;
        /** More guests than the quote covers: only `id` and `referral_token` come back, nothing was sent */
        over_capacity?: boolean;
        /** A/B variant: `instant` answers with a session and the event to open */
        quote_flow: 'instant' | 'email';
        quote_flow_reason?: string;
        eventId?: string;
        accessToken?: string;
        refreshToken?: string;
        user?: unknown;
      };
      message: string;
      emailSent: boolean;
    }>('/leads', data),

  // Returning visitor: the token goes in the body, never the URL. Answers 403 when it is invalid or expired.
  returnStatus: (id: string, token: string) =>
    apiClient.post<{ success: boolean; data: ReturnStatus }>(`/leads/${id}/return-status`, { token }),

  // Public confirmation screen: authorized by the confirmation token from create().
  // Both answer 429 with { retryAfterSeconds } when a limit is hit.
  resendQuote: (id: string, token: string) =>
    apiClient.post<{ success: boolean; data: QuoteConfirmationStatus; emailSent: boolean }>(
      `/leads/${id}/resend-quote`,
      { token }
    ),

  changeContactEmail: (id: string, token: string, contactEmail: string) =>
    apiClient.patch<{
      success: boolean;
      data: QuoteConfirmationStatus & { email: string; returnToken?: string };
      emailSent: boolean;
    }>(`/leads/${id}/contact-email`, { token, contact_email: contactEmail }),

  /** Public: "¿Cómo nos encontraste?" from the quote form's confirmation screen */
  updateReferralSources: (id: string, token: string, referralSources: string[]) =>
    apiClient.patch<{ success: boolean }>(`/leads/${id}/referral-sources`, {
      token,
      referral_sources: referralSources,
    }),

  createAdmin: (data: CreateLeadDto) => apiClient.post<Lead>('/leads/admin', data),

  convertToEvent: (id: string) =>
    apiClient.post<{
      success: boolean;
      data: { lead_id: string; event_id: string };
      message: string;
    }>(`/leads/${id}/convert`),

  getAll: (params?: {
    converted?: boolean;
    page?: number;
    limit?: number;
    search?: string;
    minGuests?: number;
    maxGuests?: number;
    portalAccess?: boolean;
    source?: 'website' | 'admin';
    quoteEmailStatus?: 'sent' | 'delivered' | 'soft_bounced' | 'bounced' | 'spam' | 'unsubscribed';
  }) => apiClient.get<LeadsListResponse>('/leads', { params }),

  getOne: (id: string) => apiClient.get<Lead>(`/leads/${id}`),

  update: (id: string, data: Partial<CreateLeadDto>) =>
    apiClient.patch<{
      success: boolean;
      data: Lead;
      message: string;
      eventUpdated?: boolean;
    }>(`/leads/${id}`, data),

  resendEmail: (id: string) =>
    apiClient.post<{
      success: boolean;
      message: string;
      data?: {
        magic_token?: string;
        token_expires_at?: string;
        plan_type?: string;
      };
    }>(`/leads/${id}/resend-email`),

  getDeletionImpact: (id: string) => apiClient.get(`/leads/${id}/deletion-impact`),

  delete: (id: string, force: boolean = false) =>
    apiClient.delete(`/leads/${id}`, {
      params: { force: force.toString() },
    }),

  /**
   * Export leads to CSV with optional filters
   * Returns a Blob containing CSV data
   */
  exportCSV: async (params?: {
    converted?: boolean;
    search?: string;
    minGuests?: number;
    maxGuests?: number;
  }): Promise<Blob> => {
    const response = await apiClient.get('/leads/export-csv', {
      params,
      responseType: 'blob',
      headers: {
        Accept: 'text/csv',
      },
    });
    return response.data;
  },

  /**
   * Toggle portal access for a lead
   */
  togglePortalAccess: (id: string, enabled: boolean) =>
    apiClient.patch<{ success: boolean; data: Lead; message: string }>(
      `/leads/${id}/portal-access`,
      { enabled }
    ),

  // ==========================================================================
  // DATA RECOVERY API
  // For clients with missing phone data (created before Dec 26, 2025)
  // ==========================================================================

  /**
   * Check if current authenticated user needs data recovery
   * @returns Lead data if recovery needed, null otherwise
   */
  checkDataRecovery: () =>
    apiClient.get<{
      success: boolean;
      data: {
        requires_recovery: boolean;
        lead?: {
          _id: string;
          bride_name: string;
          groom_name?: string;
          contact_email: string;
          contact_phone: string;
          country_code: string;
          referral_sources: string[];
          plan_type: 'weekday' | 'weekend';
          guest_count: number;
          event_date?: string;
        };
        event_status?: {
          status: string;
          prices_frozen: boolean;
          can_edit_plan_type: boolean;
          can_edit_guest_count: boolean;
        } | null;
      };
      message?: string;
    }>('/leads/check-data-recovery'),

  /**
   * Submit recovered data (phone, optional referral sources, plan_type, guest_count)
   * @param id Lead ID
   * @param data Recovery data
   */
  recoverData: (
    id: string,
    data: {
      contact_phone: string;
      country_code?: string;
      referral_sources?: string[];
      plan_type?: 'weekday' | 'weekend';
      guest_count?: number;
    }
  ) =>
    apiClient.post<{
      success: boolean;
      data: {
        lead: Lead;
        event_updated: boolean;
        changes_applied: {
          phone_updated: boolean;
          referral_updated: boolean;
          plan_type_changed: boolean;
          guest_count_changed: boolean;
        };
      };
      message: string;
    }>(`/leads/${id}/recover-data`, data),
};
